import { useQuery } from '@tanstack/react-query'
import { Activity, BarChart3, ExternalLink, FileText, Globe, Link2, ListChecks, Plus, Sparkles, Download } from 'lucide-react'
import { Link, useOutletContext } from 'react-router'

import { useEspacio, useSitios } from '../api/consultas'
import { dominioVisible } from '../api/dominios'
import { consulta, pedir } from '../api/nucleo'
import type { Lista, Version } from '../api/tipos'
import { Boton, Esqueleto, EstadoVacio, Pastilla, Tarjeta, Titulo } from '../componentes/ui'
import { direccionDeLaWeb, estadoDelSitio } from '../marco/secciones'
import { porcentaje, useDisponibilidad } from '../api/monitoreo'
import type { Contexto } from './Modulo'

const fecha = (iso: null | string | undefined) => (iso ? new Date(iso).toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' }) : null)

/**
 * «Mis sitios web»: una tarjeta por web, con su estado en llano y lo que se puede
 * hacer con ella. Lo que se ve es lo que el usuario puede leer por la API; el panel
 * no pide más de lo que va a enseñar.
 */
export const MisSitios = () => {
  const { espacio, permisos } = useOutletContext<Contexto>()
  const { data: cuenta } = useEspacio(espacio)
  const { data: sitios, isPending } = useSitios(espacio)

  // Una sola consulta para el estado de todas las webs: `where[sitio][in]`.
  const ids = (sitios ?? []).map((s) => s.id)
  const { data: versiones } = useQuery({
    queryKey: ['versiones-del-espacio', ids.join(',')],
    enabled: ids.length > 0,
    queryFn: async () =>
      (
        await pedir<Lista<Version & { sitio: number }>>(
          `/versiones-de-tema${consulta({ 'where[sitio][in]': ids.join(','), limit: 500, sort: '-numero', depth: 0 })}`,
        )
      ).docs,
  })

  const plan = cuenta?.plan && typeof cuenta.plan === 'object' ? cuenta.plan : null
  const maximo = Math.max(1, Number(plan?.sitiosMaximos) || 1)
  const deSitio = (id: number) => (versiones ?? []).filter((v) => Number(v.sitio) === id)
  // Crear webs es del dueño de la cuenta (§4); el dominio, de quien edita la web; el diseño, de quien lo ve.
  const esDueno = permisos.esDueno
  const administraLaWeb = permisos.tiene('ajustes.editar')
  const puedeCrear = esDueno && (sitios?.length ?? 0) < maximo
  // Con el plan lleno, el botón no desaparece sin más: se queda apagado y dice por qué y qué hacer.
  const planLleno = esDueno && !isPending && (sitios?.length ?? 0) >= maximo

  return (
    <>
      <Titulo
        accion={
          puedeCrear ? (
            <Link to={`/e/${espacio}/sitios/nuevo`}>
              <Boton icono={<Plus />} tipo="primario">
                Crear un sitio
              </Boton>
            </Link>
          ) : planLleno ? (
            <div className="flex flex-col items-end gap-1">
              <Boton aria-describedby="plan-lleno" disabled icono={<Plus />} tipo="primario">
                Crear un sitio
              </Boton>
              <p className="text-right text-xs text-tenue" id="plan-lleno">
                Tu plan admite {maximo === 1 ? 'una web' : `${maximo} webs`} ·{' '}
                <Link className="font-medium text-violeta underline-offset-2 hover:underline" to={`/e/${espacio}/creditos`}>
                  cambia de plan
                </Link>
              </p>
            </div>
          ) : null
        }
        descripcion={plan ? `${sitios?.length ?? 0} de ${maximo} ${maximo === 1 ? 'sitio' : 'sitios'} del plan ${plan.nombre}.` : 'Las webs de este espacio de trabajo.'}
      >
        Mis sitios web
      </Titulo>

      {isPending ? (
        <Tarjeta sinRelleno>
          <Esqueleto />
        </Tarjeta>
      ) : !sitios?.length ? (
        <EstadoVacio
          accion={
            puedeCrear ? (
              <Link to={`/e/${espacio}/sitios/nuevo`}>
                <Boton icono={<Plus />} tipo="primario">
                  Crear la primera web
                </Boton>
              </Link>
            ) : undefined
          }
          descripcion={esDueno ? 'Crea la primera y empieza a montar sus páginas.' : 'Pídele al propietario que cree la primera.'}
          icono={<Globe />}
          titulo="Todavía no hay ninguna web"
        />
      ) : (
        <ul className="grid gap-4 md:grid-cols-2">
          {sitios.map((sitio) => {
            const suyas = deSitio(sitio.id)
            const publicada = suyas.find((v) => v.publicada) ?? null
            // Una web que el asistente todavía está montando lo dice antes que nada.
            const altaEnMarcha = sitio.alta && sitio.alta.estado !== 'terminada' ? sitio.alta.estado : null
            const estado = altaEnMarcha
              ? altaEnMarcha === 'fallida'
                ? { clave: 'fallida' as const, texto: 'Alta a medias' }
                : { clave: 'construyendo' as const, texto: 'Creándose' }
              : estadoDelSitio(publicada?.numero ?? null, suyas[0] ?? null)
            // Toda web tiene dirección: la suya propia o, si no tiene, `<slug>.wixy.app`.
            const propio = sitio.dominios?.[0]?.dominio ?? null
            const dominio = dominioVisible(sitio)
            const web = direccionDeLaWeb(dominio)
            const base = `/e/${espacio}/s/${sitio.id}`
            return (
              <li key={sitio.id}>
                <Tarjeta className="flex h-full flex-col">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-violeta-suave text-violeta">
                        <Globe className="size-5" />
                      </span>
                      <div className="min-w-0">
                        <h2 className="truncate font-display text-base font-semibold">{sitio.nombre}</h2>
                        <p className="truncate text-[13px] text-tenue">
                          {dominio}
                          {!propio && administraLaWeb && (
                            <>
                              {' · '}
                              <Link className="text-violeta underline-offset-2 hover:underline" to={`${base}/dominio`}>
                                conectar tu dominio
                              </Link>
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                    <Pastilla clave={estado.clave}>{estado.texto}</Pastilla>
                  </div>
                  {estado.clave === 'publicado' && <EstadoDeDisponibilidad sitio={sitio.id} />}
                  <p className="mt-4 flex-1 text-[13px] text-tinta-2">
                    {altaEnMarcha
                      ? altaEnMarcha === 'fallida'
                        ? 'El alta se quedó a medias: la web existe y se puede volver a intentar el paso que falló.'
                        : 'El asistente está montando la web: la plantilla, las páginas y los menús.'
                      : publicada
                        ? `Última publicación el ${fecha(publicada.publicadaEn) ?? `v${publicada.numero}`}: ${publicada.comentario}`
                        : 'Cuando se publique la primera versión, aparecerá aquí.'}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2 border-t border-linea pt-4">
                    {sitio.alta && (
                      <Link to={`${base}/alta`}>
                        <Boton icono={<ListChecks />} pequeno tipo={altaEnMarcha ? 'primario' : 'secundario'}>
                          {altaEnMarcha ? 'Ver el progreso' : 'Alta'}
                        </Boton>
                      </Link>
                    )}
                    <Link to={`${base}/paginas`}>
                      <Boton icono={<FileText />} pequeno tipo={altaEnMarcha ? 'secundario' : 'primario'}>
                        Abrir
                      </Boton>
                    </Link>
                    {permisos.tiene('diseno.ver') && (
                      <Link to={`${base}/diseno`}>
                        <Boton icono={<Sparkles />} pequeno>
                          Diseño
                        </Boton>
                      </Link>
                    )}
                    {estado.clave === 'publicado' && permisos.tiene('analitica.ver') && (
                      <Link to={`${base}/visitas`}>
                        <Boton icono={<BarChart3 />} pequeno>
                          Visitas
                        </Boton>
                      </Link>
                    )}
                    {estado.clave === 'publicado' && (
                      <Link to={`${base}/disponibilidad`}>
                        <Boton icono={<Activity />} pequeno>
                          Disponibilidad
                        </Boton>
                      </Link>
                    )}
                    {administraLaWeb && (
                      <Link to={`${base}/dominio`}>
                        <Boton icono={<Link2 />} pequeno>
                          Dominio
                        </Boton>
                      </Link>
                    )}
                    {esDueno && (
                      // Todo lo de la web en un zip: su diseño con su historia y su contenido (`src/panel/exportar.ts`). Sin rehenes (§24).
                      <a download href={`/api/panel/exportar?sitio=${sitio.id}`} title="Un zip con el diseño, toda su historia y el contenido de la web">
                        <Boton icono={<Download />} pequeno tipo="discreto">
                          Exportar
                        </Boton>
                      </a>
                    )}
                    {web && estado.clave === 'publicado' && (
                      <a className="ml-auto" href={web} rel="noreferrer" target="_blank">
                        <Boton icono={<ExternalLink />} pequeno tipo="discreto">
                          Ver la web
                        </Boton>
                      </a>
                    )}
                  </div>
                </Tarjeta>
              </li>
            )
          })}
        </ul>
      )}
    </>
  )
}

/** En la tarjeta de una web publicada: si responde, y su disponibilidad de 30 días (MonitoraWeb). */
const EstadoDeDisponibilidad = ({ sitio }: { sitio: number }) => {
  const { data } = useDisponibilidad(sitio)
  const m = data?.monitor
  if (!m) return null
  const caida = m.estado === 'caida'
  return (
    <p className={caida ? 'mt-3 text-[13px] font-medium text-error' : 'mt-3 text-[13px] text-tinta-2'}>
      <span aria-hidden className={caida ? 'mr-1.5 inline-block size-2 rounded-full bg-error' : 'mr-1.5 inline-block size-2 rounded-full bg-ok'} />
      {caida ? 'No responde ahora mismo' : 'En línea'}
      {porcentaje(m.disponibilidad['30d']) ? ` · ${porcentaje(m.disponibilidad['30d'])} en 30 días` : ''}
    </p>
  )
}
